/**
 * Google Analytics 4 module for Rails + Turbolinks 5.
 *
 * Reads configuration from <meta> tags:
 *   - ga-measurement-id  : GA4 Measurement ID (e.g. G-XXXXXXXXXX)
 *   - current-user-id    : UUID of the logged-in user (optional)
 *   - current-user-role  : role string of the logged-in user (optional)
 *
 * Singleton instance is exported as the default export.
 */

class GoogleAnalytics {
  constructor() {
    this.initialized = false;
    this.measurementId = null;
    this.userId = null;
    this.userRole = null;
    this.eventQueue = []; // queue events until GA is ready
  }

  /**
   * Reads meta tags, configures GA4, and sends the initial page view.
   */
  init() {
    if (this.initialized) return; // avoid double init

    this.measurementId = this._readMeta("ga-measurement-id");
    if (!this.measurementId) return;

    this.userId = this._readMeta("current-user-id");
    this.userRole = this._readMeta("current-user-role") || "anonymous";

    this._log(`Initialized for role: ${this.userRole}`);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());

    const configParams = { send_page_view: false };
    if (this.userId) configParams.user_id = this.userId;
    configParams.user_properties = { user_role: this.userRole };

    window.gtag("config", this.measurementId, configParams);

    this.initialized = true;

    // flush queued events
    this.eventQueue.forEach(({name, params}) => this.trackEvent(name, params));
    this.eventQueue = [];
  }

  /**
   * Sends a page_view event with the current path, title, and user role.
   */
  trackPageView() {
    if (!this._ready()) return;

    const params = {
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
      user_role: this.userRole
    };

    this._log(`Page view tracked: ${params.page_path}`);
    window.gtag("event", "page_view", params);
  }

  /**
   * Sends a generic GA4 event. The user_role is automatically appended.
   *
   * @param {string} name   - Event name
   * @param {Object} params - Additional event parameters
   */
  trackEvent(name, params = {}) {
    if (!this._ready()) {
      // queue event until GA is initialized
      this.eventQueue.push({ name, params });
      return;
    }

    params.user_role = this.userRole;
    this._log(`Event tracked: ${name}`, params);
    window.gtag("event", name, params);
  }

  /**
   * Tracks a step within the contract creation funnel.
   *
   * @param {string} step         - Funnel step identifier
   * @param {string} contractType - Contract type (e.g. "NpContract")
   */
  trackContractFunnel(step, contractType) {
    this.trackEvent("contract_creation_funnel", {
      funnel_step: step,
      contract_type: contractType
    });
  }

  /**
   * Tracks the final contract_created conversion event.
   *
   * @param {string} contractType - Contract type (e.g. "NpContract")
   */
  trackContractCreated(contractType) {
    this.trackEvent("contract_created", {
      contract_type: contractType
    });
  }

  _readMeta(name) {
    const el = document.querySelector(`meta[name="${name}"]`);
    return el ? el.getAttribute("content") : null;
  }

  _ready() {
    return this.initialized && this.measurementId;
  }

  _log(message, data) {
    if (data !== undefined) {
      console.log(`[GA4] ${message}`, data);
    } else {
      console.log(`[GA4] ${message}`);
    }
  }
}

const analytics = new GoogleAnalytics();

// unified init + pageview handler
function initGA() {
  if (!analytics.initialized) analytics.init();
  analytics.trackPageView();
}

// Turbolinks + fallback for late-loaded async packs
document.addEventListener("turbolinks:load", initGA);

// fallback if script loads after DOMContentLoaded
if (document.readyState === "interactive" || document.readyState === "complete") {
  initGA();
}

export default analytics;
